import type { CSSProperties } from 'react';
import { getTimestampsFromLegacyFields } from '~factories/hours';
import { isDayLockedForUser } from '~redux/modules/dayProperties';
import type { DayProperties } from '~redux/modules/dayProperties.schema';
import { defaultState, defaultStateId } from '~redux/modules/hourStates.constants';
import type { HourState } from '~redux/modules/hourStates.schema';
import type { Hour } from '~types/hour';
import type { EnrichedProject, LightProject, Project, ProjectBase } from '~types/project';
import { prefixHexColor } from '~utils/colors';
import { isHourLocked } from '~utils/hours';
import type { SummarySource } from './util.timesheetTransformers';

/**
 * One stretch of a logged hour on the AutoSheet timeline. An hour with several
 * timestamps yields one block per timestamp.
 */
export interface LoggedHourBlock {
  /** Unique per block: `hour:<hourId>:<timestampIndex>`. */
  id: string;
  /** The hour this stretch belongs to; edits and deletes apply to the whole hour. */
  hourId: number;
  projectId: number;
  labelIds: number[];
  /** How many stretches the hour has. Its duration is only editable here when it has one. */
  timestampCount: number;
  /** False when classic would lock the hour for the viewer (see canEditLoggedHour): nothing opens it. */
  isEditable: boolean;
  /** Why the viewer can't change it and who can help (getLoggedHourLockReason); null when editable. */
  lockReason: string | null;
  projectName: string;
  projectColor: string;
  note: string;
  started_at: string;
  ended_at: string;
}

/** A table row can also represent Classic time logged without a start/end. */
export interface LoggedHourTableItem extends Omit<LoggedHourBlock, 'started_at' | 'ended_at'> {
  started_at: string | null;
  ended_at: string | null;
  durationSeconds: number;
}

const matchingLoggedHours = (
  hours: ReadonlyArray<Hour<Project | LightProject>>,
  day: string,
  userId: number,
  proposalId?: string | null
) =>
  hours.filter(
    hour =>
      hour.day === day &&
      hour.user?.id === userId &&
      !(proposalId && hour.autosheet_proposal_id === proposalId) &&
      !hour.deleted &&
      !hour.draft &&
      !hour.estimated &&
      // A running timer is still being tracked: no settled duration or range.
      hour.timer_state !== 'start'
  );

export const EMPTY_LOGGED_HOUR_BLOCKS: LoggedHourBlock[] = [];

/**
 * A logged hour's id in the table's entry selection, beside AutoSheet entry
 * ids. One per hour: an hour drawn as several stretches selects as one.
 */
export const loggedHourSelectionId = (hourId: number) => `hour:${hourId}`;

/** The hour behind a selection id, or null for an AutoSheet entry. */
export const hourIdFromSelectionId = (selectionId: string): number | null =>
  selectionId.startsWith('hour:') ? Number(selectionId.slice('hour:'.length)) : null;

/** Analytics context for edits to a logged hour from AutoSheet. */
export const LOGGED_HOUR_EDIT_CONTEXT = { interaction: 'Click Time Entry' } as const;

/** What classic's hour editor reads to decide whether the viewer may change an hour. */
export interface HourLockContext {
  hasHourStates: boolean;
  states?: Record<number, HourState | undefined>;
  userRoleId: number;
  userLedUserIds: ReadonlySet<number>;
  isAdmin: boolean;
  /** Day properties by date; a day the hour's user has locked locks its hours. */
  dayProperties?: Record<string, DayProperties[] | undefined>;
}

const NO_HOUR_STATES: HourLockContext = {
  hasHourStates: false,
  userRoleId: -1,
  userLedUserIds: new Set(),
  isAdmin: false,
};

/**
 * Why the viewer can't edit `hour` outside classic, or null when they can, by
 * classic's rules (`getHourDraftIsLocked`): they must manage it, a non-admin
 * can't change an hour on an archived project, a locked day locks its hours
 * (`getIsDraftDayLockedForUser`), and with hour states the state's lock and
 * permissions decide instead of `hour.locked`. Most people can't lift a lock
 * themselves, so each reason says who can.
 */
export const getLoggedHourLockReason = (
  hour: Hour<Project | LightProject>,
  lock: HourLockContext
): string | null => {
  if (hour.manage === false) {
    return "You don't have permission to change this time entry. If it needs changing, ask your manager or an admin.";
  }
  if (hour.project.active === false && !lock.isAdmin) {
    return "This time entry's project is archived. If it needs changing, ask an admin to restore the project.";
  }
  if (isDayLockedForUser(hour.user.id, lock.dayProperties?.[hour.day])) {
    return 'This day is locked. If this time entry needs changing, ask your manager or an admin to unlock the day.';
  }
  if (!lock.hasHourStates) {
    return hour.locked
      ? 'This time entry is locked. If it needs changing, ask your manager or an admin to unlock it.'
      : null;
  }
  const isLocked = isHourLocked(hour, {
    states: lock.states,
    userRoleId: lock.userRoleId,
    userIsTeamLead: lock.userLedUserIds.has(hour.user.id),
  });
  if (!isLocked) return null;
  // The state isHourLocked judged by.
  const state = lock.states?.[hour.state?.id ?? defaultStateId] ?? hour.state ?? defaultState;
  return state.locked
    ? `This time entry is marked ${state.name}, which locks it. If it needs changing, ask your manager or an admin to change its status.`
    : `Your role can't change time entries marked ${state.name}. If it needs changing, ask your manager or an admin.`;
};

/** Whether the viewer may edit `hour` outside classic (see getLoggedHourLockReason). */
export const canEditLoggedHour = (
  hour: Hour<Project | LightProject>,
  lock: HourLockContext
): boolean => getLoggedHourLockReason(hour, lock) === null;

/** The per-hour fields shared by timeline blocks and table rows. */
const loggedHourFields = (hour: Hour<Project | LightProject>, lock: HourLockContext) => {
  const lockReason = getLoggedHourLockReason(hour, lock);
  return {
    hourId: hour.id,
    projectId: hour.project.id,
    labelIds: hour.label_ids ?? [],
    isEditable: lockReason === null,
    lockReason,
    projectName: hour.project.name,
    projectColor: prefixHexColor(hour.project.color || 'cccccc'),
    note: hour.note ?? '',
  };
};

/**
 * Hours the AutoSheet day view must show next to its own entries: the user's
 * logged time for `day` that its entries don't already stand for (classic
 * calendar, API, CLI, MCP). Hours submitted from the proposal being drawn are
 * left out, since its entries are already on the timeline. An hour submitted
 * from another proposal stays in: Rails keeps `autosheet_proposal_id` when an
 * hour is edited, so an AutoSheet hour moved to another day still carries its
 * old day's proposal id, and no entry on this day stands for it.
 *
 * Placement follows classic: an hour's `timestamps`, or its legacy `from`/`to`
 * when it has none.
 */
export const getLoggedHoursForDay = (
  hours: ReadonlyArray<Hour<Project | LightProject>>,
  day: string,
  userId: number,
  proposalId?: string | null,
  lock: HourLockContext = NO_HOUR_STATES
): LoggedHourBlock[] => {
  const matching = matchingLoggedHours(hours, day, userId, proposalId);
  if (matching.length === 0) return EMPTY_LOGGED_HOUR_BLOCKS;

  const blocks: LoggedHourBlock[] = [];
  for (const hour of matching) {
    // Hours logged as a bare duration have no start/end to place them by.
    const timestamps = getTimestampsFromLegacyFields(hour).filter(ts => ts.from && ts.to);
    const fields = loggedHourFields(hour, lock);
    timestamps.forEach((timestamp, index) => {
      blocks.push({
        id: `hour:${hour.id}:${index}`,
        ...fields,
        timestampCount: timestamps.length,
        started_at: timestamp.from,
        ended_at: timestamp.to,
      });
    });
  }
  blocks.sort((a, b) => Date.parse(a.started_at) - Date.parse(b.started_at));
  return blocks;
};

/** Keep duration-only hours visible and editable in the table without inventing timeline ranges. */
export const getLoggedHourRowsForDay = (
  hours: ReadonlyArray<Hour<Project | LightProject>>,
  day: string,
  userId: number,
  proposalId?: string | null,
  lock: HourLockContext = NO_HOUR_STATES
): LoggedHourTableItem[] => {
  const matching = matchingLoggedHours(hours, day, userId, proposalId);
  const blocks = getLoggedHoursForDay(hours, day, userId, proposalId, lock);
  // Classic counts an hour's duration, which an API or CLI hour can set apart
  // from its span. Each stretch gets its share of it, so totals match classic.
  const spanOf = (block: LoggedHourBlock) =>
    (Date.parse(block.ended_at) - Date.parse(block.started_at)) / 1000;
  const spanByHour = new Map<number, number>();
  for (const block of blocks) {
    spanByHour.set(block.hourId, (spanByHour.get(block.hourId) ?? 0) + spanOf(block));
  }
  const hourSeconds = new Map(matching.map(hour => [hour.id, hour.duration.total_seconds]));
  const timed = blocks.map(block => {
    const seconds = hourSeconds.get(block.hourId) ?? spanOf(block);
    const totalSpan = spanByHour.get(block.hourId) ?? 0;
    return {
      ...block,
      durationSeconds:
        block.timestampCount <= 1 || totalSpan <= 0
          ? seconds / Math.max(block.timestampCount, 1)
          : (seconds * spanOf(block)) / totalSpan,
    };
  });
  const untimed = matching
    .filter(hour => getTimestampsFromLegacyFields(hour).length === 0)
    .map(hour => ({
      id: `hour:${hour.id}:untimed`,
      ...loggedHourFields(hour, lock),
      timestampCount: 0,
      started_at: null,
      ended_at: null,
      durationSeconds: hour.duration.total_seconds,
    }));
  return [...timed, ...untimed];
};

/** Logged hours as summary sources, so the day/week totals and breakdowns count them. */
export const loggedHourSummarySources = (
  rows: ReadonlyArray<LoggedHourTableItem>
): SummarySource[] =>
  rows.map(row => ({
    projectId: row.projectId,
    labelIds: row.labelIds,
    duration: { total_seconds: row.durationSeconds },
    projectName: row.projectName,
    projectColor: row.projectColor,
  }));

/** Total seconds of logged-hour table rows. */
export const sumLoggedHourSeconds = (rows: ReadonlyArray<LoggedHourTableItem>): number =>
  rows.reduce((sum, row) => sum + row.durationSeconds, 0);

interface Range {
  id: string;
  started_at: string;
  ended_at: string;
}

/**
 * Logged-hour ranges that don't intersect `range`. An entry can already sit on
 * top of logged time (AutoSheet generation doesn't look at logged hours), and
 * such a logged hour must not act as a sibling for that entry: the editor would
 * refuse to save it and a resize would snap it onto the logged hour's edge.
 * Logged hours the entry doesn't touch still stop it from growing into them.
 */
export const loggedRangesClearOf = <T extends Range>(
  loggedRanges: T[],
  range: { started_at?: string | null; ended_at?: string | null } | null | undefined
): T[] => {
  const startMs = range?.started_at ? Date.parse(range.started_at) : Number.NaN;
  const endMs = range?.ended_at ? Date.parse(range.ended_at) : Number.NaN;
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return loggedRanges;
  return loggedRanges.filter(
    logged => !(Date.parse(logged.started_at) < endMs && startMs < Date.parse(logged.ended_at))
  );
};

/** Horizontal slot of a timeline block among the blocks it shares time with. */
export interface LaneColumn {
  index: number;
  count: number;
}

export const FULL_LANE_COLUMN: LaneColumn = { index: 0, count: 1 };

/**
 * Side-by-side columns for timeline blocks that overlap in time, so none is
 * drawn under another. Logged time can sit under an AutoSheet entry
 * (generation doesn't look at logged hours) or under another logged hour, and
 * that double-logged time is what the user most needs to see.
 *
 * Each run of transitively overlapping blocks shares one column count; a block
 * takes the leftmost column free at its start. Ties on start keep input order,
 * so pass entries first to keep them on the left. Blocks overlapping nothing
 * get no entry: callers fall back to `FULL_LANE_COLUMN`.
 */
export const getOverlapColumns = (ranges: ReadonlyArray<Range>): Map<string, LaneColumn> => {
  const items = ranges
    .map((range, order) => ({
      id: range.id,
      startMs: Date.parse(range.started_at),
      endMs: Date.parse(range.ended_at),
      order,
    }))
    .filter(item => Number.isFinite(item.startMs) && item.endMs > item.startMs)
    .sort((a, b) => a.startMs - b.startMs || a.order - b.order);

  const columns = new Map<string, LaneColumn>();
  let run: Array<{ id: string; index: number }> = [];
  let columnEndsMs: number[] = [];
  let runEndMs = Number.NEGATIVE_INFINITY;
  const closeRun = () => {
    if (columnEndsMs.length > 1) {
      for (const { id, index } of run) columns.set(id, { index, count: columnEndsMs.length });
    }
    run = [];
    columnEndsMs = [];
  };

  for (const item of items) {
    if (item.startMs >= runEndMs) closeRun();
    let index = columnEndsMs.findIndex(endMs => endMs <= item.startMs);
    if (index === -1) index = columnEndsMs.length;
    columnEndsMs[index] = item.endMs;
    run.push({ id: item.id, index });
    runEndMs = Math.max(runEndMs, item.endMs);
  }
  closeRun();
  return columns;
};

/** Width and offset of a block's `SummaryBlock` for its lane column. */
export const laneColumnStyle = ({ index, count }: LaneColumn): CSSProperties => ({
  width: count === 1 ? '100%' : `${100 / count}%`,
  marginLeft: index === 0 ? 0 : `${(100 * index) / count}%`,
});

/**
 * The hour may sit on a project AutoSheet doesn't offer (logged in classic, or
 * one the viewer has since left): add it so a project picker can still name
 * it, from the hour's own copy until the full project loads.
 */
export function withLoggedHourProject(
  projects: EnrichedProject[],
  block: Pick<LoggedHourBlock, 'projectId' | 'projectName' | 'projectColor'>,
  loadedProject: Project | null | undefined
): EnrichedProject[] {
  if (projects.some(p => p.base.id === block.projectId)) {
    return projects;
  }
  // Only the hour's own project; a picker may have loaded another one since.
  const loaded = loadedProject?.id === block.projectId ? loadedProject : null;
  const base: ProjectBase = loaded ?? {
    id: block.projectId,
    name: block.projectName,
    color: block.projectColor.replace(/^#/, ''),
    active: true,
    client: { id: 0, name: '', color: '', active: true } as ProjectBase['client'],
  };
  return [...projects, { base, isFullyLoaded: !!loaded, fullProject: loaded }];
}
